import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Trash2, AlertTriangle, RefreshCw, Database, Users, CalendarDays, FileText, Bell, Tag, Clock, Trophy, Loader2, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminDataManagement = () => {
  const { token } = useAuth();
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmAction, setConfirmAction] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [result, setResult] = useState(null);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchCounts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/data-management/counts`, { headers });
      if (res.ok) setCounts(await res.json());
    } catch (_) {}
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  const executeDelete = async (endpoint, key) => {
    setDeleting(key);
    setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/data-management/${endpoint}`, { method: 'DELETE', headers });
      if (res.ok) {
        const data = await res.json();
        setResult({ success: true, message: data.message });
        fetchCounts();
      } else {
        setResult({ success: false, message: 'Fehler beim Löschen.' });
      }
    } catch (_) {
      setResult({ success: false, message: 'Netzwerkfehler.' });
    }
    setDeleting(null);
    setConfirmAction(null);
  };

  const categories = [
    {
      key: 'bookings_expired',
      icon: Clock,
      title: 'Abgelaufene / Ausstehende Buchungen',
      description: 'Alle Buchungen mit Status "expired" oder "pending"',
      endpoint: 'bookings-expired',
      color: 'text-amber-600',
      bg: 'bg-amber-50',
    },
    {
      key: 'bookings_cancelled',
      icon: CalendarDays,
      title: 'Stornierte / Abgelehnte Buchungen',
      description: 'Alle Buchungen mit Status "cancelled" oder "rejected"',
      endpoint: 'bookings-cancelled',
      color: 'text-gray-600',
      bg: 'bg-gray-50',
    },
    {
      key: 'bookings_all',
      icon: Database,
      title: 'ALLE Buchungen',
      description: 'Alle Buchungen inkl. bezahlte und bestätigte (+ Zahlungen)',
      endpoint: 'bookings-all',
      color: 'text-red-600',
      bg: 'bg-red-50',
      danger: true,
    },
    {
      key: 'customers',
      icon: Users,
      title: 'Alle Kunden-Accounts',
      description: 'Alle Benutzer mit Rolle "Kunde" (nicht Admin/Mitarbeiter)',
      endpoint: 'customers',
      color: 'text-red-600',
      bg: 'bg-red-50',
      danger: true,
    },
    {
      key: 'inquiries',
      icon: FileText,
      title: 'Anfragen & Offerten',
      description: 'Alle Anfragen und zugehoerige Offerten',
      endpoint: 'inquiries',
      countLabel: (c) => `${c.inquiries} Anfragen + ${c.offers} Offerten`,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
    },
    {
      key: 'notifications',
      icon: Bell,
      title: 'Benachrichtigungen',
      description: 'Alle In-App Benachrichtigungen aller Benutzer',
      endpoint: 'notifications',
      color: 'text-sky-600',
      bg: 'bg-sky-50',
    },
    {
      key: 'discount_codes',
      icon: Tag,
      title: 'Rabattcodes',
      description: 'Alle generierten Rabattcodes (Follow-up, Empfehlungen etc.)',
      endpoint: 'discount-codes',
      color: 'text-purple-600',
      bg: 'bg-purple-50',
    },
    {
      key: 'waitlist',
      icon: Clock,
      title: 'Warteliste',
      description: 'Alle Wartelisten-Einträge für nicht verfügbare Standorte',
      endpoint: 'waitlist',
      color: 'text-teal-600',
      bg: 'bg-teal-50',
    },
    {
      key: 'competition_entries',
      icon: Trophy,
      title: 'Wettbewerb-Einträge',
      description: 'Alle Teilnahmen am Wettbewerb',
      endpoint: 'competition',
      color: 'text-orange-600',
      bg: 'bg-orange-50',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--primary))]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-data-management">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Daten-Management</h2>
          <p className="text-[hsl(var(--muted-foreground))] mt-1">
            Test- und Altdaten bereinigen. Gelöschte Daten können nicht wiederhergestellt werden.
          </p>
        </div>
        <Button variant="outline" onClick={fetchCounts} data-testid="refresh-counts">
          <RefreshCw className="w-4 h-4 mr-2" />
          Aktualisieren
        </Button>
      </div>

      <Alert className="bg-red-50 border-red-200">
        <AlertTriangle className="h-4 w-4 text-red-600" />
        <AlertDescription className="text-red-700">
          <strong>Achtung:</strong> Gelöschte Daten können nicht wiederhergestellt werden. Erstellen Sie vorher ein Backup falls noetig.
        </AlertDescription>
      </Alert>

      {result && (
        <Alert className={result.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
          <CheckCircle className={`h-4 w-4 ${result.success ? 'text-green-600' : 'text-red-600'}`} />
          <AlertDescription className={result.success ? 'text-green-700' : 'text-red-700'}>
            {result.message}
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-4">
        {categories.map((cat, i) => {
          const Icon = cat.icon;
          const count = cat.countLabel ? cat.countLabel(counts || {}) : (counts?.[cat.key] ?? 0);
          const countNum = typeof count === 'number' ? count : (counts?.[cat.key] ?? 0);
          const isConfirming = confirmAction === cat.key;

          return (
            <motion.div
              key={cat.key}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              <Card className={`${cat.danger ? 'border-red-200' : ''}`}>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-11 h-11 rounded-xl ${cat.bg} flex items-center justify-center`}>
                        <Icon className={`w-5 h-5 ${cat.color}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-sm">{cat.title}</h3>
                        <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{cat.description}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <span className="text-lg font-bold tabular-nums" data-testid={`count-${cat.key}`}>
                        {typeof count === 'string' ? count : count.toLocaleString('de-CH')}
                      </span>

                      {isConfirming ? (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-red-600 font-medium">Sicher?</span>
                          <Button
                            size="sm"
                            variant="destructive"
                            disabled={deleting === cat.key}
                            onClick={() => executeDelete(cat.endpoint, cat.key)}
                            data-testid={`confirm-delete-${cat.key}`}
                          >
                            {deleting === cat.key ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              'Ja, löschen'
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setConfirmAction(null)}
                          >
                            Abbrechen
                          </Button>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant={cat.danger ? 'destructive' : 'outline'}
                          disabled={countNum === 0}
                          onClick={() => setConfirmAction(cat.key)}
                          data-testid={`delete-${cat.key}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                          Löschen
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminDataManagement;
